import Cropper from 'cropperjs';
import { fetchImageUsingCapacitorHTTP } from '../../../imageFetchUtils';

// Matches the JPEG/WebP export quality already used elsewhere in the image editor flow.
// We keep this slightly below 1.0 to avoid oversized uploads without a visible quality drop.
export const DEFAULT_LOSSY_IMAGE_QUALITY = 0.85;
const CROPPER_RESTRICT_TO_CANVAS_VIEW_MODE = 2;

type CroppedImageBlobOptions = {
    imageUrl: string;
    inputImageType: string;
    outputImageType: string;
    imageEditorData: Cropper.Data;
};

/**
 * Draw an image object URL into a canvas so the editor can read a data URL for Cropper.
 */
export const loadImageIntoCanvas = (objectUrl: string, imageType: string): Promise<{ dataUrl: string }> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            const ctx = canvas.getContext('2d');
            if (!ctx) return reject(new Error('Unable to load image into canvas'));
            ctx.drawImage(img, 0, 0);
            const dataUrl = canvas.toDataURL(imageType);

            resolve({ dataUrl });
        };

        img.onerror = reject;
        img.src = objectUrl;
        img.alt = 'To edit';
    });
};

/**
 * Create hidden DOM nodes for Cropper, which needs a mounted image to calculate crop bounds.
 */
const createOffscreenContainer = (): { container: HTMLDivElement; image: HTMLImageElement } => {
    const container = document.createElement('div');
    // Keep the image mounted for Cropper measurements without ever showing it in the viewport.
    container.style.position = 'fixed';
    container.style.left = '-10000px';
    container.style.top = '-10000px';

    const image = document.createElement('img');
    container.appendChild(image);
    document.body.appendChild(container);

    return { container, image };
};

/**
 * Load an image source into an existing image element before Cropper reads its dimensions.
 */
const loadImageSourceIntoImage = (image: HTMLImageElement, imageSource: string): Promise<void> => {
    return new Promise((resolve, reject) => {
        image.onload = () => resolve();
        image.onerror = reject;
        image.src = imageSource;
    });
};

/**
 * Apply saved editor crop data to a mounted image and export the cropped result as a blob.
 */
const cropImageToBlob = (
    image: HTMLImageElement,
    imageEditorData: Cropper.Data,
    outputImageType: string,
): Promise<Blob> => {
    return new Promise((resolve, reject) => {
        const cropper = new Cropper(image, {
            // Keep the crop selection fully inside the rendered image bounds.
            viewMode: CROPPER_RESTRICT_TO_CANVAS_VIEW_MODE,
            autoCrop: true,
            autoCropArea: 1,
            checkOrientation: false,
            ready: () => {
                cropper.setData(imageEditorData);
                cropper
                    .getCroppedCanvas({
                        width: imageEditorData.width,
                        height: imageEditorData.height,
                        maxWidth: 4096,
                        maxHeight: 4096,
                    })
                    .toBlob(
                        (blob: Blob | null) => {
                            cropper.destroy();
                            if (!blob) return reject(new Error('Failed to produce cropped image blob'));
                            resolve(blob);
                        },
                        outputImageType,
                        DEFAULT_LOSSY_IMAGE_QUALITY,
                    );
            },
        });
    });
};

/**
 * Download an image, mount it offscreen, and return the saved crop area as a new image blob.
 */
export const getCroppedImageBlobFromUrl = async ({
    imageUrl,
    inputImageType,
    outputImageType,
    imageEditorData,
}: CroppedImageBlobOptions): Promise<Blob> => {
    const { objectUrl } = await fetchImageUsingCapacitorHTTP(imageUrl, inputImageType);
    const { container, image } = createOffscreenContainer();

    try {
        await loadImageSourceIntoImage(image, objectUrl);
        return await cropImageToBlob(image, imageEditorData, outputImageType);
    } finally {
        URL.revokeObjectURL(objectUrl);
        container.remove();
    }
};
